import {
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { generatePath, Link } from "react-router-dom";
import RelativeDate from "../components/RelativeDate";
import API from "../api";
import { useResource } from "../useResource";
import { environmentPagePath } from "./EnvironmentPage";
import { Add, Delete, Info } from "@material-ui/icons";
import ConfirmDialog from "../components/ConfirmDialog";
import EnvironmentCreateDialog from "./EnvironmentCreateDialog";
import { Environment } from "../_generated";
import axios from "axios";
import { BackendURL } from "../globals";
import { authToken } from "../components/Authentication";
import { ProjectResource } from "../models/ProjectResource";
import analytics from "../analytics";

export default function EnvironmentListPage(props: { projectName: string }) {
  const [project] = useResource(API.projects.read, props.projectName);
  const [environments] = useResource(API.environments.list, props.projectName);
  const [services] = useResource(API.services.list, props.projectName);

  const [confirmID, setConfirmID] = useState(-1);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const [resources, setResources] = useState([] as ProjectResource[]);

  axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

  useEffect(() => {
    analytics.page("EnvironmentList");
  }, []);

  useEffect(() => {
    if (environments.length === 0) {
      setShowCreateDialog(true);
    }
  }, [environments]);

  useEffect(() => {
    const url = `${BackendURL}/api/projects/${props.projectName}/resources/`;
    //const url = "https://run.mocky.io/v3/4a2fd42d-d4d6-4a68-97d3-8eafd0a4674e";
    axios.get(url).then((response) => {
      setResources(response.data.results);
    });
  }, [props.projectName]);

  async function onEnvironmentCreate(environment: Environment, setErrors: any) {
    let newEnvId;
    try {
      const res = await axios.post(
        `${BackendURL}/api/projects/${props.projectName}/environments/`,
        {
          ...environment,
        }
      );
      newEnvId = res.data["pk"];
    } catch (err) {
      console.log(err.response);
      const reason = err.response.data["reason"];

      if (reason === "keys_are_not_valid") {
        setErrors({
          general: "AWS Keys are invalid or you do not have enough permissions",
        });
      } else {
        setErrors({
          general: "An unknown error occured, please try again later",
        });
      }
      return;
    }

    setShowCreateDialog(false);
    window.location.href = `#/projects/${props.projectName}/deployments`;
    await axios.post(
      `${BackendURL}/api/projects/${props.projectName}/environments/${newEnvId}/applyAndDeploy/`
    );
  }

  async function onDeleteConfirm(id: number) {
    await axios.post(
      `${BackendURL}/api/projects/${props.projectName}/environments/${id}/destroy/`
    );
    setConfirmID(-1);
  }

  return (
    <>
      <Grid container spacing={2} justify="center" alignItems="stretch">
        {environments.map((environment) => (
          <Grid item key={environment.pk} xs={12} md={6} lg={4}>
            <Link
              to={generatePath(environmentPagePath, {
                project_name: props.projectName,
                environment_id: String(environment.pk),
              })}
              style={{
                textDecoration: "none",
                pointerEvents:
                  environment.status === "DESTROYING" ? "none" : "auto",
              }}
            >
              <Card
                variant="outlined"
                style={{
                  backgroundColor:
                    environment.status !== "COMPLETED" ? "#eeeeee" : "inherit",
                }}
              >
                <CardContent>
                  <Grid
                    container
                    spacing={0}
                    alignItems="center"
                    justify="space-between"
                  >
                    <Grid item>
                      <Typography variant="h6">
                        {environment.alias || environment.name}
                        &nbsp;&nbsp;
                        <Chip size="small" label={environment.target} />
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="caption">
                        <RelativeDate date={environment.date_updated} />
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        {environment.region}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Grid container alignItems="baseline" justify="space-between">
                    <Grid item>
                      {environment.status === "COMPLETED" && (
                        <Button
                          size="medium"
                          variant="text"
                          startIcon={<Info />}
                        >
                          Details
                        </Button>
                      )}
                      {environment.status === "DESTROYING" && (
                        <Button size="medium" variant="outlined" disabled>
                          Destroying...
                        </Button>
                      )}
                      {environment.status === "PENDING" && (
                        <Button size="medium" variant="outlined" disabled>
                          Creating...
                        </Button>
                      )}
                    </Grid>

                    <Grid item>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          setConfirmID(environment.pk!);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            </Link>
          </Grid>
        ))}
        <Grid item xs={12} md={6} lg={4} container>
          <Button
            startIcon={<Add />}
            variant="outlined"
            style={{ flexGrow: 1 }}
            onClick={() => setShowCreateDialog(true)}
          >
            Add environment
          </Button>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmID !== -1}
        id={confirmID}
        onCancel={() => setConfirmID(-1)}
        onConfirm={(id) => onDeleteConfirm(id)}
        text="This will delete the environment and remove all deployed components from the cloud. Do you want to proceed?"
      />

      <EnvironmentCreateDialog
        project={project}
        open={showCreateDialog}
        services={services}
        resources={resources}
        isFirst={environments.length === 0}
        onSubmit={onEnvironmentCreate}
        onCancel={() => setShowCreateDialog(false)}
      />
    </>
  );
}
